import { Box, Icon, Typography } from "@outschool/backpack";
import {
  ClassDetailsNoSectionsQuery,
  ClassDetailsQuery_ActivityFragment,
} from "@outschool/gql-frontend-generated";
import {
  IconDefinition,
  faBirthdayCake,
  faUser,
  faUsers,
} from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { Space } from "@outschool/ui-legacy-component-library";
import React, { ReactNode } from "react";

import { IconBullet } from "../PriceDetailsComponents";
import { PriceDetailsClassDetails, useActivityString } from "..";

export interface ActivityDetailsProps {
  activity: ClassDetailsNoSectionsQuery["activity"];
}

export function ActivityDetails({ activity }: ActivityDetailsProps) {
  return (
    <Box
      sx={{
        display: "block",
      }}
    >
      <ActivityAges activity={activity} />
      <Space y="small" />
      <ActivitySize activity={activity} />
      <Space y="medium" />
      <PriceDetailsClassDetails activity={activity} />
    </Box>
  );
}

export interface InfoBadgeContentProps {
  activity:
    | ClassDetailsNoSectionsQuery["activity"]
    | ClassDetailsQuery_ActivityFragment;
  useIconBullet?: boolean;
}

export function ActivityAges({ activity }: InfoBadgeContentProps) {
  const { t } = useTranslation(
    "ui-components-website\\ClassDetailsPage\\ActivityDetails"
  );
  const { ageRangeString } = useActivityString();

  return (
    <InfoBadge
      icon={faBirthdayCake}
      title={ageRangeString(activity, false)}
      description={
        activity.age_min && activity.age_max && t("year old learners")
      }
    />
  );
}

export function ActivitySize({
  activity,
  useIconBullet = false,
}: InfoBadgeContentProps) {
  const { t } = useTranslation(
    "ui-components-website\\ClassDetailsPage\\ActivityDetails"
  );
  const clubEnrollmentCount = activity.clubSection?.validEnrollmentCount ?? 0;

  const { classSizeString } = useActivityString();
  const count = activity.isClub
    ? clubEnrollmentCount
    : classSizeString(activity);

  const unit = activity.isClub
    ? t("active learner", {
        count: clubEnrollmentCount,
        defaultValue_plural: `active learners`,
      })
    : activity.size_min
    ? t("learner per class", {
        count: !!activity.size_max ? activity.size_max : activity.size_min,
        defaultValue_plural: `learners per class`,
      })
    : null;

  return useIconBullet ? (
    <IconBullet
      icon={activity.size_max == 1 ? faUser : faUsers}
      text={[count, unit].join(" ")}
    />
  ) : (
    <InfoBadge
      icon={activity.size_max == 1 ? faUser : faUsers}
      title={count}
      description={unit}
    />
  );
}

interface InfoBadgeProps {
  icon: IconDefinition;
  title: ReactNode;
  description: ReactNode;
}

export function InfoBadge({ icon, title, description }: InfoBadgeProps) {
  return (
    <Box
      flex
      sx={{
        alignItems: "center",
        gap: "0.5em",
      }}
    >
      <Icon icon={icon} fixedWidth />
      <Box
        flex
        sx={{
          alignItems: "baseline",
          columnGap: "0.5em",
          flexWrap: "wrap",
        }}
      >
        {title && (
          <Typography variant="h4" component="span">
            {title}
          </Typography>
        )}
        {description && (
          <Box
            sx={{
              color: "grey.700",
            }}
          >
            {description}
          </Box>
        )}
      </Box>
    </Box>
  );
}
